import { Link } from '@tanstack/react-router'

import { formatRelative } from '@/lib/utils/format'
import type { RepositoryRow } from '@/types/domain'

const LANGUAGE_COLORS: Record<string, string> = {
  JavaScript: '#f1e05a',
  TypeScript: '#3178c6',
  HTML: '#e34c26',
  CSS: '#563d7c',
  Python: '#3572A5',
  EJS: '#a91e50',
  Java: '#b07219',
  'C++': '#f34b7d',
  Go: '#00ADD8',
  Rust: '#dea584',
}

function getLanguageColor(lang: string) {
  return LANGUAGE_COLORS[lang] || '#8b949e'
}

/**
 * One repository.
 *
 * The open-issue count carries a caveat rather than a bare number: GitHub's own counter
 * includes pull requests, and quoting it as "open issues" is the kind of wrong answer that
 * looks completely right.
 */
export function RepoCard({ repo }: { repo: RepositoryRow }) {
  const [owner, name] = repo.full_name.split('/')

  return (
    <Link
      to="/repositories/$owner/$repo"
      params={{ owner: owner ?? '', repo: name ?? '' }}
      className="group flex flex-col gap-2 border-b border-line px-4 py-3.5 transition-colors
        hover:bg-hover sm:border-b-0 sm:border-r sm:last:border-r-0 min-w-0"
    >
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="min-w-0 flex-1">
          <p className="mono truncate text-sm text-ink group-hover:text-primary-bright">
            {name ?? repo.full_name}
          </p>
          <p className="mono truncate text-ink-faint">{owner}</p>
        </div>
        <span
          className="shrink-0 rounded-sm border border-line-strong px-1.5 py-0.5 font-mono
            text-micro text-ink-muted"
          title={repo.private ? 'Not publicly visible' : 'Public repository'}
        >
          {repo.private ? 'private' : 'public'}
        </span>
      </div>

      {repo.description ? (
        <p className="line-clamp-2 text-xs text-ink-muted">{repo.description}</p>
      ) : null}

      <dl className="mt-auto flex flex-wrap items-baseline gap-x-5 gap-y-1 pt-1">
        <div className="flex items-baseline gap-1.5">
          <dd
            className="font-mono text-md text-ink"
            title="GitHub's own counter, which includes open pull requests"
          >
            {repo.open_issues_count}
          </dd>
          <dt className="text-xs text-ink-muted">open</dt>
        </div>
        {repo.language ? (
          <div className="flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: getLanguageColor(repo.language) }}
              aria-hidden="true"
            />
            <dd className="text-xs text-ink-dim">{repo.language}</dd>
          </div>
        ) : null}
        <div className="ml-auto text-xs text-ink-faint">{formatRelative(repo.pushed_at)}</div>
      </dl>
    </Link>
  )
}
